import MicroModal from 'micromodal';
import { createContext, Fragment, FunctionComponent, h, render } from 'preact';
import { useContext, useEffect, useRef, useState } from 'preact/hooks';
import { getApi } from '../../../src/ts/libs/getApi';
import { Error } from '../error/Error';
import { useFetch } from '../hooks/useFetch';
import { BasketItemProps, BasketProps } from '../types/basket';
import { currency } from '../../../src/ts/libs/currency';
import { Alert } from '../common/Alert';

const Basket = createContext<BasketProps>({});
const BasketEditItem: FunctionComponent<
  Pick<BasketItemProps, 'quantity' | 'alternateSellingUnits' | 'id' | 'price'> & { hideEdit?: boolean }
> = ({ quantity, alternateSellingUnits, id, price, hideEdit }) => {
  const { setBasket } = useContext(Basket);
  const [qty, setQuantity] = useState(+quantity);
  const [sellingUnitId, setSellingUnitId] = useState(alternateSellingUnits.id);
  const { response, fetchData: updateQuantity } = useFetch(`${getApi('updateBasketQuantity')}`, {});
  const {
    response: responseSu,
    fetchData: updateSellingUnit,
    error: updateSellingUnitError,
  } = useFetch(`${getApi('updateSellingUnit')}`, {});

  // if we updat quantity then update basket
  useEffect(() => {
    if (response && response.hasOwnProperty('items')) {
      setBasket(() => ({ ...response }));
    }
  }, [response]);
  useEffect(() => {
    if (responseSu && responseSu.hasOwnProperty('items')) {
      setBasket(() => ({ ...responseSu }));
    }
  }, [responseSu]);

  const handleChangeOne = (evt) => {
    const { value } = evt.currentTarget;

    updateQuantity(
      {
        method: 'PUT',
      },
      {
        productCode: id,
        quantity: value,
        sellingUnitId,
      }
    );

    setQuantity(value);
  };

  const handleRemoveOne = () => {
    // set the value first
    setQuantity((c) => {
      const newVal = c === 1 ? 1 : +c - 1;

      // update api
      updateQuantity(
        {
          method: 'PUT',
        },
        {
          productCode: id,
          quantity: newVal,
          sellingUnitId,
        }
      );

      // optimistically set qunatity
      return newVal;
    });
  };
  const handleAddOne = () => {
    // set the value first
    setQuantity((c) => {
      const newVal = +c + 1;

      // update api
      updateQuantity(
        {
          method: 'PUT',
        },
        {
          productCode: id,
          quantity: newVal,
          sellingUnitId,
        }
      );

      // optimistically set qunatity
      return newVal;
    });
  };
  const handleChangeUnit = (e) => {
    const old = sellingUnitId;

    setSellingUnitId(e.currentTarget.value);

    updateSellingUnit(
      {
        method: 'PUT',
      },
      {
        previousSellingUnitId: old,
        newSellingUnitId: e.currentTarget.value,
      }
    );
  };

  return (
    <div className="basket-card__form is-active" data-test="your-order-edit-item">
      <form className="form">
        {!hideEdit ? (
          <div className="form-field">
            <label htmlFor="quantity">Quantity</label>
            <div className="quantity-control">
              <button
                type="button"
                className="button button--muted"
                onClick={handleRemoveOne}
                data-test="your-order-edit-item-minus"
              >
                <span className="button__label  sr-only">Minus one from quantity</span>
                <svg aria-hidden="true" width={12} height={4} xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M11.333 3.037c0 .244-.2.444-.444.444H1.11a.446.446 0 01-.444-.444V.963c0-.244.2-.444.444-.444h9.778c.244 0 .444.2.444.444v2.074z"
                    fill="currentColor"
                    fillRule="evenodd"
                  />
                </svg>
              </button>
              <input
                name="quantity"
                id="quantity"
                value={qty}
                type="number"
                onChange={handleChangeOne}
                aria-label="Enter Quanity"
                data-test="your-order-edit-item-quantity"
              />
              <button
                type="button"
                className="button button--muted"
                onClick={handleAddOne}
                data-test="your-order-edit-item-plus"
              >
                <span className="button__label  sr-only">Plus one to quantity</span>
                <svg aria-hidden="true" width={12} height={12} xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M11.333 7.037c0 .244-.2.444-.444.444H7.48v3.408c0 .244-.2.444-.444.444H4.963a.446.446 0 01-.444-.444V7.48H1.11a.446.446 0 01-.444-.444V4.963c0-.244.2-.444.444-.444H4.52V1.11c0-.244.2-.444.444-.444h2.074c.244 0 .444.2.444.444V4.52h3.408c.244 0 .444.2.444.444v2.074z"
                    fill="currentColor"
                    fillRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <span></span>
        )}
        {!hideEdit && alternateSellingUnits?.sellingUnits && alternateSellingUnits?.sellingUnits.length >= 1 ? (
          <div className="form-field">
            <label htmlFor="sellingUnit">Selling unit</label>

            <select
              name="sellingUnit"
              id="sellingUnit"
              className="select"
              onChange={handleChangeUnit}
              value={sellingUnitId}
              data-test="your-order-edit-item-selling-unit"
            >
              {alternateSellingUnits?.sellingUnits.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <span></span>
        )}
        <span className="basket-card__form__total">{currency(+price * qty)}</span>
      </form>
    </div>
  );
};
const BasketCard: FunctionComponent<BasketItemProps> = ({
  name,
  price,
  code,
  unitPrice,
  quantity,
  alternateSellingUnits,
  img,
  id,
  url,
}) => {
  const isFirstRender = useRef(true);
  const { setBasket } = useContext(Basket);
  const { response, fetchData: removeItem } = useFetch(`${getApi('removeFromBasket')}`, {});
  const handleRemove = (productCode, sellingUnit) => {
    console.debug('FED log: Handle remove clicked');
    console.debug('FED log: Removing: ', productCode, sellingUnit);
    removeItem(
      {
        method: 'DELETE',
      },
      {
        productCode,
        sellingUnitId: sellingUnit,
      }
    );
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; // Skip running the effect on the first render
    }
    if (response && response.hasOwnProperty('items')) {
      setBasket(() => ({ ...response }));
    }
    const event = new Event('basket-update');
    window.dispatchEvent(event);
  }, [response]);

  // const { name: sellingUnit } = sellingUnits?.find((u) => u?.active);
  const isSOS = code === 'SOS';
  return (
    <div
      className={
        isSOS
          ? 'basket-card basket-card--order basket-card--no-img basket-card--reverse'
          : 'basket-card basket-card--order'
      }
      data-test={isSOS ? 'your-order-edit-item-sos' : 'your-order-edit-item-card'}
    >
      <picture className="basket-card__img">
        <img src={img ? img : '/assets/img/no-photo-product.png'} alt={name} />
      </picture>

      <div className="basket-card__content">
        <div className="basket-card__body">
          <div className="basket-card__header">
            <h3 className="basket-card__heading">
              {!isSOS && url ? <a href={url}>{name}</a> : <Fragment>{name}</Fragment>}
            </h3>

            {!isSOS && (
              <div className="basket-card__remove">
                <button
                  type="button"
                  className="button button--transparent"
                  onClick={() => handleRemove(id, alternateSellingUnits.id)}
                  data-test="your-order-edit-item-remove"
                >
                  <span className="button__label button__label--underline sr-only">Remove item</span>
                  <svg aria-hidden="true" width={14} height={16} xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M13.5 1H9.75L9.456.416A.75.75 0 008.784 0H5.212a.741.741 0 00-.668.416L4.25 1H.5a.5.5 0 00-.5.5v1a.5.5 0 00.5.5h13a.5.5 0 00.5-.5v-1a.5.5 0 00-.5-.5zM1.662 14.594A1.5 1.5 0 003.16 16h7.682a1.5 1.5 0 001.497-1.406L13 4H1l.663 10.594z"
                      fill="currentColor"
                      fillRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
          {!isSOS && (
            <ul className="basket-card__meta">
              {code && <li>Code: {code}</li>}
              {unitPrice && <li>Unit price: {currency(+unitPrice)}</li>}
            </ul>
          )}
          {isSOS && (
            <div className="basket-card__msg" data-test="your-order-sos-msg">
              You will be charged a small order handling fee of £12.00 if your order is less than £150 (excluding VAT).
            </div>
          )}
          <BasketEditItem
            alternateSellingUnits={alternateSellingUnits}
            quantity={quantity}
            id={id}
            price={unitPrice}
            hideEdit={isSOS}
          />
        </div>
        <div className="basket-card__footer">
          <span className="basket-card__total" data-test="your-order-edit-item-total">
            <strong>{currency(+price)}</strong> Ex VAT
          </span>
        </div>
      </div>
    </div>
  );
};
const CartItems = () => {
  const { items } = useContext(Basket);
  return (
    <Fragment>
      {items?.length >= 1 && items.map((item) => <BasketCard key={item.id} {...item} />)}

      {items?.length === 0 && <p data-test="your-order-no-items">No items in your basket</p>}
    </Fragment>
  );
};

const CartOverview = () => {
  const { vat, total, totalIncVat, items, restrictedProduct } = useContext(Basket);
  const [loading, setLoading] = useState(false);
  const [buttonText, setButtonText] = useState('Checkout now');
  const hasSOS = items && items.length && items.some((item) => item.code === 'SOS');

  const handleModalOpen = () => {
    MicroModal.show('request-quote', { disableScroll: true });
  };

  const handleNext = () => {
    console.debug('FED log: CartOverview handle next clicked');
    setButtonText('Loading');
    setLoading(true);
    setTimeout(() => {
      window.location.href = '/your-order/delivery-options/';
    }, 200);
    return true;
  };

  useEffect(() => {
    console.debug('FED log: CartOverview loading state updated, loading is:', loading);
  }, [loading]);

  return (
    <div className="panel order-overview" data-test="your-order-overview">
      <h3 className="order-overview__heading">Total</h3>
      <p className="order-overview__meta">
        <strong>Subtotal</strong>
        {total ? <span>{currency(+total)}</span> : <span className="price-loader"></span>}
      </p>
      {hasSOS && (
        <p className="order-overview__meta order-overview__meta--small" data-test="your-order-overview-sos">
          <small>(includes small order surcharge)</small>
        </p>
      )}
      <p className="order-overview__meta order-overview__meta--border">
        <strong>VAT</strong>
        {vat >= 0 ? <span>{currency(+vat)}</span> : <span className="price-loader"></span>}
      </p>
      <p className="order-overview__meta  order-overview__meta--border  order-overview__meta--primary">
        <strong>Total</strong>
        {totalIncVat ? <span>{currency(+totalIncVat)}</span> : <span className="price-loader price-loader--lg"></span>}
      </p>
      {/* {restrictedProduct && <p className="order-overview__meta">restricted Product</p>} */}
      {total ? (
        <a
          href="/your-order/delivery-options/"
          className="button order-overview__checkout-btn button--lg button--full"
          data-test="your-order-overview-checkout"
        >
          <svg width={14} height={16} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path
              d="M12.5 7h-.75V4.75A4.756 4.756 0 007 0a4.756 4.756 0 00-4.75 4.75V7H1.5A1.5 1.5 0 000 8.5v6A1.5 1.5 0 001.5 16h11a1.5 1.5 0 001.5-1.5v-6A1.5 1.5 0 0012.5 7zM9.25 7h-4.5V4.75C4.75 3.51 5.76 2.5 7 2.5s2.25 1.01 2.25 2.25V7z"
              fill="currentColor"
              fillRule="evenodd"
            />
          </svg>
          <span className="button__label">{buttonText}</span>
        </a>
      ) : (
        <span />
      )}
      <img
        src="/assets/img/payment-logos.png"
        alt="Visa, Mastercard and Opayo accepted"
        className="order-overview__logos"
      />
      <button
        type="button"
        onClick={handleModalOpen}
        className="order-overview__quote-request"
        data-test="your-order-request-quote"
      >
        Request a quote for this order
      </button>
    </div>
  );
};

const Controls = () => {
  const { handleRemoveAll, count, basketQty } = useContext(Basket);
  if (count <= 0) {
    return null;
  }
  return (
    <div className="order-controls">
      <p className="order-controls__total">
        You have {basketQty} {basketQty >= 2 ? 'items' : 'item'} in your order
      </p>
      <div className="order-controls__actions">
        {/* <button type="button" className="button button--transparent">
      <span className="button__label  ">Import Spreadsheet</span>
      <svg aria-hidden="true" width={16} height={16} xmlns="http://www.w3.org/2000/svg">
        <path d="M.5 9c-.275 0-.5.225-.5.5v1c0 .275.225.5.5.5H4V9H.5zm15.281-5.719L12.721.22a.75.75 0 00-.53-.219H12v4h4v-.19a.748.748 0 00-.219-.529zM11 4.25V0H4.75A.748.748 0 004 .75V9h4V6.963c0-.447.54-.67.856-.354l2.988 3.016a.532.532 0 010 .75l-2.99 3.012a.501.501 0 01-.857-.353V11H4v4.25c0 .416.334.75.75.75h10.5c.416 0 .75-.334.75-.75V5h-4.25a.752.752 0 01-.75-.75z" fill="currentColor" fillRule="evenodd" />
      </svg>
    </button> */}
        <button
          type="button"
          className="button button--transparent"
          onClick={handleRemoveAll}
          data-test="basket-remove-all"
        >
          <span className="button__label  ">Remove all</span>
          <svg aria-hidden="true" width={14} height={16} xmlns="http://www.w3.org/2000/svg">
            <path
              d="M13.5 1H9.75L9.456.416A.75.75 0 008.784 0H5.212a.741.741 0 00-.668.416L4.25 1H.5a.5.5 0 00-.5.5v1a.5.5 0 00.5.5h13a.5.5 0 00.5-.5v-1a.5.5 0 00-.5-.5zM1.662 14.594A1.5 1.5 0 003.16 16h7.682a1.5 1.5 0 001.497-1.406L13 4H1l.663 10.594z"
              fill="currentColor"
              fillRule="evenodd"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};
const YourOrder = () => {
  const [basket, setBasket] = useState<BasketProps>({});
  const { response, error, loading, fetchData } = useFetch(`${getApi('getBasket')}`, {});
  const { response: removalAllResponse, fetchData: removeAll } = useFetch(`${getApi('removeAllFromBasket')}`, {});

  useEffect(() => {
    if (response && response.hasOwnProperty('items')) {
      setBasket(() => ({ ...response }));
    }
  }, [response]);
  useEffect(() => {
    if (removalAllResponse && removalAllResponse.hasOwnProperty('items')) {
      setBasket(() => ({ ...removalAllResponse }));
    }
  }, [removalAllResponse]);

  useEffect(() => {
    (async function getBasket() {
      await fetchData({});
    })();
  }, []);

  const handleUpdateBasket = async () => {
    await fetchData({});
  };
  const handleRemoveAll = async () => {
    await removeAll({ method: 'DELETE' });
    const event = new Event('basket-update');
    window.dispatchEvent(event);
  };

  useEffect(() => {
    window?.addEventListener('basket-update', handleUpdateBasket);
    return () => {
      // Clean up the subscription
      window?.removeEventListener('basket-update', handleUpdateBasket);
    };
  }, []);

  const basketQty = basket?.items?.filter((item) => item.code !== 'SOS').length;
  const { restrictedProduct } = basket;
  return (
    <Basket.Provider value={{ ...basket, setBasket, handleRemoveAll, basketQty }}>
      <Fragment>
        {error && <Error message="Sorry something went wrong" />}

        <Controls />

        <div className="layout-main-and-side">
          <div className="layout-main-and-side__main">
            {restrictedProduct && (
              <Alert message="Please note: A license is required to purchase some products in your basket." />
            )}
            <CartItems />
          </div>
          <div className="layout-main-and-side__side">
            <CartOverview />
          </div>
        </div>
      </Fragment>
    </Basket.Provider>
  );
};

try {
  const domContainer = document.querySelector('#your-order');
  if (domContainer) {
    const inner = domContainer.querySelector('.layout-main-and-side');
    render(<YourOrder />, domContainer, inner);
  }
} catch (e) {
  console.warn(e);
}
